import { api } from "@/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, PropsWithChildren, useContext } from "react";
import { getCajaActual } from "../api/get-caja-actual";
import { assignCaja } from "../api/assign-caja";
import { openCaja, OpenCajaRequest } from "../api/open-caja";
import { closeCaja, CloseCajaRequest } from "../api/close-caja";
import { useAuthContext } from "@/features/auth/contexts/auth-context";
import { CajaApertura } from "@/shared/types/caja-apertura";
import { Permiso } from "@/shared/types/permiso";

const LOCAL_STORAGE_CAJA_KEY = "caja";
const getStorageKey = (organizacionId?: string) => `${LOCAL_STORAGE_CAJA_KEY}:${organizacionId}`;

type CajaData = {
    id: string,
    nombre: string,
    abierto: boolean
}

type LocalData = CajaData & {
    token: string
}

export interface CajaContextReturn {
    assign: (cajaId: string) => Promise<CajaData>,
    revoke: () => Promise<void>,
    open: (request: OpenCajaRequest) => Promise<CajaApertura>,
    close: (request: CloseCajaRequest) => Promise<CajaApertura>,
    reload: () => Promise<void>,
    hasCajaAssigned: boolean,
    isCajaOpen: boolean,
    cajaData: CajaData,
    loading: boolean,
    error: Error | undefined
}

const CajaContext = createContext<CajaContextReturn>({
    assign: async () => null!,
    revoke: async () => { },
    open: async () => null!,
    close: async () => null!,
    reload: async () => { },
    hasCajaAssigned: false,
    cajaData: null!,
    isCajaOpen: false,
    error: undefined,
    loading: false,
});

export function useCajaContext() {
    return useContext(CajaContext);
}

export function CajaContextProvider({
    children
}: PropsWithChildren) {
    const { isLoggedIn, hasPermisos, authData } = useAuthContext();
    const organizacionId = authData?.organizacionId;

    // RootAdmin no necesita caja asignada
    const isRootAdmin = hasPermisos(Permiso.ManageOrganizaciones);

    const queryClient = useQueryClient();
    const { data: cajaData, isLoading, error, refetch } = useQuery({
        queryFn: async () => {
            try {
                const localData = getLocalData();
                if (!localData) {
                    delete api.defaults.headers.common["X-Caja-Token"];
                    return null;
                }

                const response = await getCajaActual(localData.token);
                setLocalData(response);
                return response;
            } catch (e) {
                deleteLocalData();
                return null;
            }
        },
        queryKey: ["caja", organizacionId],
        enabled: isLoggedIn && !isRootAdmin
    })

    const assign = async (cajaId: string) => {
        const cajaData = await assignCaja(cajaId);
        setLocalData(cajaData);
        queryClient.setQueryData(["caja", organizacionId], cajaData);
        return cajaData;
    }

    const revoke = async () => {
        deleteLocalData();
        queryClient.setQueryData(["caja", organizacionId], null);
    }

    const open = async (request: OpenCajaRequest) => {
        const result = await openCaja(request);
        queryClient.setQueryData(["caja", organizacionId], { ...cajaData, abierto: true } as CajaData);
        return result;
    }

    const close = async (request: CloseCajaRequest) => {
        const result = await closeCaja(request);
        queryClient.setQueryData(["caja", organizacionId], { ...cajaData, abierto: false } as CajaData);
        return result;
    }

    const reload = async () => {
        await refetch();
    }

    //Private
    const getLocalData = () => {
        if (!organizacionId) return undefined;
        const stringData = localStorage.getItem(getStorageKey(organizacionId));
        if (!stringData) return undefined;
        return JSON.parse(stringData) as LocalData;
    }

    const setLocalData = (data: LocalData) => {
        localStorage.setItem(getStorageKey(organizacionId), JSON.stringify(data));
        api.defaults.headers.common["X-Caja-Token"] = `${data.token}`
    }

    const deleteLocalData = () => {
        localStorage.removeItem(getStorageKey(organizacionId));
        delete api.defaults.headers.common["X-Caja-Token"];
    }

    // Para RootAdmin, devolver valores por defecto que no requieren caja
    if (isRootAdmin) {
        return <CajaContext.Provider value={{
            assign: async () => null!,
            revoke: async () => { },
            open: async () => null!,
            close: async () => null!,
            reload: async () => { },
            hasCajaAssigned: true, // RootAdmin no necesita caja
            cajaData: null!,
            isCajaOpen: true, // RootAdmin no está limitado por caja
            error: undefined,
            loading: false,
        }}>
            {children}
        </CajaContext.Provider>
    }

    return <CajaContext.Provider value={{
        assign: assign,
        revoke: revoke,
        open: open,
        close: close,
        reload: reload,
        hasCajaAssigned: Boolean(cajaData),
        cajaData: cajaData!,
        isCajaOpen: Boolean(cajaData?.abierto),
        error: error || undefined,
        loading: isLoading,
    }}>
        {children}
    </CajaContext.Provider>
}